import React, { useCallback, useEffect, useState } from 'react';
import { Mic, Play, ShieldAlert, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { recordingAPI } from '../api';
import { useAuth } from '../context/AuthContext';

const fmtBytes = (b) => (!b ? '—' : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`);
const fmtDur = (s) => (s == null ? '—' : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`);

// Admin playback: the <audio> src is a 60-second signed stream URL, never a permanent/public link.
export const CallRecordings = () => {
  const { user, hasPermission } = useAuth();
  const canView = user?.role === 'super_admin' || hasPermission?.('call.recording.view');
  const canDelete = user?.role === 'super_admin' || hasPermission?.('call.manage');
  const [state, setState] = useState({ loading: true, items: [], retention: null, denied: false });
  const [playing, setPlaying] = useState(null); // { call_id, url }

  const load = useCallback(async () => {
    if (!canView) { setState({ loading: false, items: [], retention: null, denied: true }); return; }
    try {
      const r = await recordingAPI.list();
      setState({ loading: false, items: r.data.items || [], retention: r.data.retention_days, denied: false });
    } catch (e) {
      setState({ loading: false, items: [], retention: null, denied: e?.response?.status === 403 });
    }
  }, [canView]);
  useEffect(() => { load(); }, [load]);

  const play = async (rec) => {
    try {
      const r = await recordingAPI.playbackToken(rec.call_id);
      const base = process.env.REACT_APP_BACKEND_URL;
      setPlaying({ call_id: rec.call_id, url: `${base}${r.data.stream_url}` });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Playback not allowed'); }
  };

  const remove = async (rec) => {
    if (!window.confirm('Delete this recording permanently?')) return;
    try { await recordingAPI.remove(rec.call_id); toast.success('Recording deleted'); setPlaying(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  if (!canView || state.denied) {
    return (
      <div className="clay-card p-4 text-sm text-muted-foreground flex items-center gap-2" data-testid="recordings-denied">
        <ShieldAlert className="h-4 w-4" /> Call recordings require the “View Call Recordings” permission.
      </div>
    );
  }

  return (
    <div className="clay-card p-4" data-testid="call-recordings">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-medium flex items-center gap-2"><Mic className="h-4 w-4 text-primary" /> Call Recordings</h3>
        {state.retention && <span className="text-xs text-muted-foreground">Auto-deleted after {state.retention} days</span>}
      </div>
      {state.loading ? <p className="text-sm text-muted-foreground">Loading…</p>
        : state.items.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="recordings-empty">No recordings yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-muted-foreground">
              <tr><th className="py-2">Lead / Number</th><th>Result</th><th>Duration</th><th>Size</th><th>Uploaded</th><th>By</th><th></th></tr>
            </thead>
            <tbody>
              {state.items.map((r) => (
                <React.Fragment key={r.recording_id}>
                  <tr className="border-t border-slate-100 dark:border-slate-800" data-testid="recording-row">
                    <td className="py-2">{r.lead_name || r.phone_number || '—'}</td>
                    <td>{r.call_result || '—'}</td>
                    <td>{fmtDur(r.duration_s)}</td>
                    <td>{fmtBytes(r.bytes)}</td>
                    <td className="text-xs">{r.completed_at ? new Date(r.completed_at).toLocaleString() : '—'}</td>
                    <td className="text-xs">{r.uploaded_by_name || '—'}</td>
                    <td className="whitespace-nowrap">
                      <button onClick={() => play(r)} data-testid={`recording-play-${r.call_id}`}
                        className="inline-flex items-center gap-1 rounded-lg bg-primary px-2.5 py-1 text-xs text-white hover:opacity-90">
                        <Play className="h-3 w-3" /> Play
                      </button>
                      {canDelete && (
                        <button onClick={() => remove(r)} data-testid={`recording-delete-${r.call_id}`}
                          className="ml-2 text-red-500" title="Delete recording"><Trash2 className="h-3.5 w-3.5" /></button>
                      )}
                    </td>
                  </tr>
                  {playing?.call_id === r.call_id && (
                    <tr data-testid="recording-player-row">
                      <td colSpan={7} className="pb-3">
                        <audio key={playing.url} src={playing.url} controls autoPlay preload="metadata"
                          className="w-full" data-testid="recording-player" />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CallRecordings;
